'use client'

import { createContext, useContext, useEffect, useMemo, useState } from 'react'

// The topbar owns the current page's refresh controls (plan decision 4):
// "updated Xm ago", the auto-refresh interval and the refresh button live in
// one place at the top right, whatever page is open. A page registers its
// state with usePageRefresh (BaseDashboard does it for every dashboard built
// on it) and the Topbar renders RefreshControl from it; when the page
// unmounts the slot empties.

export type PageRefreshState = {
  lastUpdated?: number | null
  onRefresh?: () => void
  refreshing?: boolean
  storageKey?: string
}

type PageChrome = {
  refresh: PageRefreshState | null
  setRefresh: (state: PageRefreshState | null) => void
}

const PageChromeContext = createContext<PageChrome>({ refresh: null, setRefresh: () => {} })

export function PageChromeProvider({ children }: { children: React.ReactNode }) {
  const [refresh, setRefresh] = useState<PageRefreshState | null>(null)
  const value = useMemo(() => ({ refresh, setRefresh }), [refresh])
  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>
}

export function usePageChrome(): PageChrome {
  return useContext(PageChromeContext)
}

/** Registers this page's refresh state with the topbar while mounted. */
export function usePageRefresh(state: PageRefreshState) {
  const { setRefresh } = usePageChrome()
  const { lastUpdated, onRefresh, refreshing, storageKey } = state
  useEffect(() => {
    if (lastUpdated == null && !onRefresh) { setRefresh(null); return }
    setRefresh({ lastUpdated, onRefresh, refreshing, storageKey })
  }, [lastUpdated, onRefresh, refreshing, storageKey, setRefresh])
  useEffect(() => () => setRefresh(null), [setRefresh])
}
